import { stripIndent } from "common-tags";
import { CustomerPromotionCode } from "./components/CustomerPromotionCode";

export const customerPromotionCodeEmailTemplate = {
    label: 'Customer Promotion Code',
    description: 'Sent when a customer receives a promotion code',
    icon: 'Tag',
    component: CustomerPromotionCode,
    defaultTemplate: {
        name: 'Customer Promotion Code',
        subject: {
            en: 'Your special discount code from {{ storeName }}',
            fr: 'Votre code de réduction spécial de {{ storeName }}',
            it: 'Il tuo codice sconto speciale da {{ storeName }}',
            es: 'Tu código de descuento especial de {{ storeName }}',
            de: 'Dein spezieller Rabattcode von {{ storeName }}',
        },
        templateVariables: {
            customer_promotion_code: {
                body: {
                    en: stripIndent`<div style="text-align: center; max-width: 600px; margin: 0 auto;">
                  <p>Hello {{ customerFirstName }},</p>
                  <p>We're excited to offer you a special promotion code for your next purchase!</p>
                  <p>Use the code <strong>{{ promotionCode }}</strong> at checkout to receive your discount.
                     {{ #expiresAt }}This code expires on {{ expiresAt }}.{{ /expiresAt }}</p>
                  <p>Thank you for being a valued customer!</p>
                  <p>Best regards,<br/>The {{ storeName }} Team</p>
                </div>`,
                    fr: stripIndent`<div style="text-align: center; max-width: 600px; margin: 0 auto;">
                  <p>Bonjour {{ customerFirstName }},</p>
                  <p>Nous sommes ravis de vous offrir un code promotionnel spécial pour votre prochain achat !</p>
                  <p>Utilisez le code <strong>{{ promotionCode }}</strong> lors de votre commande pour bénéficier de votre réduction.
                     {{ #expiresAt }}Ce code expire le {{ expiresAt }}.{{ /expiresAt }}</p>
                  <p>Merci d'être un client fidèle !</p>
                  <p>Cordialement,<br/>L'équipe {{ storeName }}</p>
                </div>`,
                    it: stripIndent`<div style="text-align: center; max-width: 600px; margin: 0 auto;">
                  <p>Ciao {{ customerFirstName }},</p>
                  <p>Siamo entusiasti di offrirti un codice promozionale speciale per il tuo prossimo acquisto!</p>
                  <p>Utilizza il codice <strong>{{ promotionCode }}</strong> al momento del checkout per ricevere il tuo sconto.
                     {{ #expiresAt }}Questo codice scade il {{ expiresAt }}.{{ /expiresAt }}</p>
                  <p>Grazie per essere un cliente prezioso!</p>
                  <p>Cordiali saluti,<br/>Il team di {{ storeName }}</p>
                </div>`,
                    es: stripIndent`<div style="text-align: center; max-width: 600px; margin: 0 auto;">
                  <p>Hola {{ customerFirstName }},</p>
                  <p>¡Estamos encantados de ofrecerte un código promocional especial para tu próxima compra!</p>
                  <p>Utiliza el código <strong>{{ promotionCode }}</strong> al finalizar la compra para recibir tu descuento.
                     {{ #expiresAt }}Este código vence el {{ expiresAt }}.{{ /expiresAt }}</p>
                  <p>¡Gracias por ser un cliente valioso!</p>
                  <p>Saludos cordiales,<br/>El equipo de {{ storeName }}</p>
                </div>`,
                    de: stripIndent`<div style="text-align: center; max-width: 600px; margin: 0 auto;">
                  <p>Hallo {{ customerFirstName }},</p>
                  <p>Wir freuen uns, dir einen speziellen Aktionscode für deinen nächsten Einkauf anzubieten!</p>
                  <p>Verwende den Code <strong>{{ promotionCode }}</strong> beim Checkout, um deinen Rabatt zu erhalten.
                     {{ #expiresAt }}Dieser Code läuft am {{ expiresAt }} ab.{{ /expiresAt }}</p>
                  <p>Vielen Dank, dass du ein geschätzter Kunde bist!</p>
                  <p>Beste Grüße,<br/>Das {{ storeName }} Team</p>
                </div>`,
                },
            },
        },
    },
    variables: [
        { key: 'customerFirstName', label: 'Customer First Name', example: 'John' },
        { key: 'customerLastName', label: 'Customer Last Name', example: 'Doe' },
        { key: 'customerEmail', label: 'Customer Email', example: 'john@example.com' },
        { key: 'storeName', label: 'Store Name', example: 'My Store' },
        { key: 'promotionCode', label: 'Promotion Code', example: 'SAVE20' },
        { key: 'expiresAt', label: 'Expiration Date', example: '2023-12-31' },
    ],
    utmParameters: {
        utmSource: 'email',
        utmMedium: 'promotion',
        utmCampaign: 'customer_promotion',
        utmContent: (template: any) => template.params?.specific?.promotionType || 'discount',
    },
    specificParams: {
        promotionType: {
            type: 'select',
            label: 'Promotion Type',
            options: [
                { value: 'discount', label: 'Discount' },
                { value: 'free_shipping', label: 'Free Shipping' },
                { value: 'gift', label: 'Free Gift' },
            ],
            default: 'discount',
        },
    },
    mockData: (storeName = 'Demo Store') => ({
        promotionCode: {
            id: 'promo_preview',
            code: 'SAVE20',
            promotion: {
                name: 'Summer Sale',
                description: '20% off your next purchase',
            },
            expiresAt: new Date('2024-12-31'),
            store: {
                id: 'store_preview',
                name: storeName,
                type: 'store',
                accountId: 'acc_preview',
                credentials: {},
                presentmentCurrencies: ['EUR'],
                chargeCurrencies: ['EUR'],
                baseCurrency: 'EUR',
            },
            customer: {
                firstName: 'John',
                lastName: 'Doe',
                email: 'john@example.com',
            },
        },
    }),
    defaultMessages: {
        promotion_title: 'Your Special Offer',
        promotion_subtitle: 'Use this code for your next purchase',
        cta_button: 'Shop Now',
        footer_text: '© 2024 All rights reserved',
    },
}
